import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import PageHeader from './PageHeader';
import Breadcrumbs from './Breadcrumbs';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
const IconBack = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);
const IconSettings = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const IconCheck = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);
const IconX = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconClan = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);
const IconRequests = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);
const IconUsers = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const IconSearch = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const IconPause = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconPlay = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const IconAlert = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Search input
// ---------------------------------------------------------------------------
function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="relative mb-4">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-500">
        <IconSearch />
      </div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-9 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
        >
          <IconX />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
function Toast({ message, type, onDismiss }) {
  React.useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const colors = type === 'success'
    ? 'bg-green-900/90 border-green-600 text-green-200'
    : 'bg-red-900/90 border-red-600 text-red-200';
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-xl ${colors}`}>
      <span className="text-sm">{message}</span>
      <button onClick={onDismiss} className="opacity-60 hover:opacity-100"><IconX /></button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDialog
// ---------------------------------------------------------------------------
function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <h3 className="text-base font-semibold text-gray-100 mb-2">{title}</h3>
        <p className="text-sm text-gray-400 mb-5">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${confirmClass || 'bg-blue-600 hover:bg-blue-500'}`}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'clans', label: 'Clans', icon: <IconClan /> },
  { id: 'requests', label: 'Join Requests', icon: <IconRequests /> },
  { id: 'members', label: 'Members', icon: <IconUsers /> },
];

// ---------------------------------------------------------------------------
// Delete Confirmation Modal
// ---------------------------------------------------------------------------
function DeleteClanModal({ clan, onConfirm, onCancel, t }) {
  const [confirmation, setConfirmation] = useState('');
  const expected = `delete ${clan.slug}`;
  const canDelete = confirmation.toLowerCase().trim() === expected.toLowerCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-800 border border-red-700/50 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="text-red-500">
            <IconAlert />
          </div>
          <h3 className="text-lg font-bold text-red-400">{t('delete_clan_title') || 'Delete Clan Data'}</h3>
        </div>

        <div className="space-y-4 text-sm text-gray-300">
          <p>
            {t('delete_clan_warning') || 'You are about to delete ALL data for'} <strong className="text-white">{clan.display_name}</strong>.
          </p>
          <div className="bg-red-900/20 border border-red-700/40 rounded-lg p-4 space-y-2">
            <p className="font-semibold text-red-300">{t('delete_clan_will_delete') || 'This will permanently delete:'}</p>
            <ul className="list-disc list-inside space-y-1 text-red-200/80">
              <li>{t('delete_clan_entry') || 'The clan entry itself'}</li>
              <li>{t('delete_clan_war_data') || 'All war history and attack data'}</li>
              <li>{t('delete_clan_scores') || 'All season scores and points'}</li>
              <li>{t('delete_clan_events') || 'All events and donations'}</li>
              <li>{t('delete_clan_members') || 'All member registrations'}</li>
              <li>{t('delete_clan_settings') || 'All clan rules and settings'}</li>
              <li>{t('delete_clan_user_profiles') || 'All user accounts associated with this clan'}</li>
            </ul>
          </div>
          <p className="text-gray-400">
            {t('delete_clan_irreversible') || 'This action is irreversible. The clan and all its data will be completely removed.'}
          </p>
          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
            <p className="mb-2 font-medium text-gray-200">
              {t('delete_clan_confirm_instruction') || 'To confirm, type:'} <code className="bg-gray-800 px-2 py-1 rounded text-yellow-300">delete {clan.slug}</code>
            </p>
            <input
              type="text"
              value={confirmation}
              onChange={e => setConfirmation(e.target.value)}
              placeholder={`delete ${clan.slug}`}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={() => canDelete && onConfirm(clan.id, clan.slug)}
            disabled={!canDelete}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              canDelete
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-red-900/40 text-red-400 cursor-not-allowed'
            }`}
          >
            {t('delete_clan_confirm') || 'Delete All Data'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClansTab
// ---------------------------------------------------------------------------
function ClansTab({ clans, loading, onToggleActive, onDelete, t }) {
  const [search, setSearch] = useState('');
  const [clanToDelete, setClanToDelete] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return clans;
    const q = search.toLowerCase();
    return clans.filter(c =>
      (c.display_name || '').toLowerCase().includes(q) ||
      (c.clan_tag || '').toLowerCase().includes(q)
    );
  }, [clans, search]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map(i => (
          <div key={i} className="h-20 bg-gray-800/60 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search by clan name or tag…" />
      {!filtered.length ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-4xl mb-3">🏰</div>
          <p>{search ? 'No clans match your search.' : 'No clans configured.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(clan => (
            <div key={clan.id} className={`flex items-center justify-between gap-4 bg-gray-800/60 border rounded-2xl px-5 py-4 ${clan.is_active ? 'border-gray-700' : 'border-red-900/50 opacity-75'}`}>
              <div className="min-w-0">
                <div className="font-semibold text-gray-100 text-sm flex items-center gap-2">
                  {clan.display_name}
                  {!clan.is_active && (
                    <span className="text-xs px-2 py-0.5 bg-red-900/60 text-red-300 rounded-full border border-red-700/40">
                      {t('clan_paused') || 'Paused'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 font-mono mt-0.5">{clan.clan_tag}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => onToggleActive(clan.id, !clan.is_active)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    clan.is_active
                      ? 'bg-yellow-900/40 hover:bg-yellow-900/60 text-yellow-300 border border-yellow-700/40'
                      : 'bg-green-900/40 hover:bg-green-900/60 text-green-300 border border-green-700/40'
                  }`}
                  title={clan.is_active ? (t('pause_clan_tip') || 'Pause data fetching') : (t('resume_clan_tip') || 'Resume data fetching')}
                >
                  {clan.is_active ? <IconPause /> : <IconPlay />}
                  <span>{clan.is_active ? (t('pause') || 'Pause') : (t('resume') || 'Resume')}</span>
                </button>
                <Link
                  to={`/settings/${clan.slug}`}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                >
                  <IconSettings />
                  <span>Settings</span>
                </Link>
                <button
                  onClick={() => setClanToDelete(clan)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-700/40 rounded-lg transition-colors"
                  title={t('delete_clan_tip') || 'Delete all clan data'}
                >
                  <IconTrash />
                  <span>{t('delete') || 'Delete'}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {clanToDelete && (
        <DeleteClanModal
          clan={clanToDelete}
          onConfirm={(clanId, slug) => {
            onDelete(clanId, slug);
            setClanToDelete(null);
          }}
          onCancel={() => setClanToDelete(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status badge colours
// ---------------------------------------------------------------------------
const STATUS_COLORS = {
  approved: 'bg-green-900/40 text-green-300 border-green-700/40',
  pending:  'bg-yellow-900/40 text-yellow-300 border-yellow-700/40',
  rejected: 'bg-gray-700/40 text-gray-400 border-gray-600/40',
  banned:   'bg-red-900/40 text-red-400 border-red-700/40',
};

// ---------------------------------------------------------------------------
// MembersTab
// ---------------------------------------------------------------------------
function MembersTab({ members, loading, onBan, onUnban }) {
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter(m =>
      (m.player_name || '').toLowerCase().includes(q) ||
      (m.coc_player_tag || '').toLowerCase().includes(q) ||
      (m.clan_name || '').toLowerCase().includes(q) ||
      (m.clan_tag || '').toLowerCase().includes(q)
    );
  }, [members, search]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-800/60 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      <SearchBar value={search} onChange={setSearch} placeholder="Search by player, tag or clan…" />
      {!filtered.length ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-4xl mb-3">👥</div>
          <p>{search ? 'No members match your search.' : 'No registered users yet.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => (
            <div key={m.user_id} className="flex items-center justify-between gap-4 bg-gray-800/60 border border-gray-700 rounded-xl px-5 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-100 truncate">
                  {m.player_name || m.coc_player_tag || m.user_id}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                  {m.coc_player_tag && (
                    <span className="font-mono">{m.coc_player_tag}</span>
                  )}
                  {m.clan_name && (
                    <>
                      <span className="text-gray-700">·</span>
                      <span>{m.clan_name}</span>
                    </>
                  )}
                  {m.clan_tag && (
                    <span className="font-mono text-gray-600">{m.clan_tag}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs font-medium px-2 py-0.5 rounded border ${STATUS_COLORS[m.status] || STATUS_COLORS.rejected}`}>
                  {m.status}
                </span>
                {m.status === 'banned' ? (
                  <button
                    onClick={() => setConfirm({
                      title: 'Unban User',
                      message: `Unban ${m.player_name || m.coc_player_tag || m.user_id}? They will be able to re-register.`,
                      confirmLabel: 'Unban',
                      confirmClass: 'bg-green-600 hover:bg-green-500',
                      onConfirm: () => { onUnban(m.user_id); setConfirm(null); },
                    })}
                    className="px-3 py-1.5 text-xs bg-green-900/40 hover:bg-green-700/60 text-green-300 border border-green-700/40 rounded-lg transition-colors"
                  >
                    Unban
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirm({
                      title: 'Ban User',
                      message: `Ban ${m.player_name || m.coc_player_tag || m.user_id}? They will be blocked from logging in.`,
                      confirmLabel: 'Ban',
                      confirmClass: 'bg-red-600 hover:bg-red-500',
                      onConfirm: () => { onBan(m.user_id); setConfirm(null); },
                    })}
                    className="px-3 py-1.5 text-xs bg-red-900/40 hover:bg-red-700/60 text-red-400 border border-red-700/40 rounded-lg transition-colors"
                  >
                    Ban
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RequestsTab
// ---------------------------------------------------------------------------
function RequestsTab({ requests, loading, onApprove, onReject }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return requests;
    const q = search.toLowerCase();
    return requests.filter(r =>
      (r.player_name || '').toLowerCase().includes(q) ||
      (r.player_tag || '').toLowerCase().includes(q) ||
      (r.clan_name || '').toLowerCase().includes(q) ||
      (r.clan_tag || '').toLowerCase().includes(q) ||
      (r.message || '').toLowerCase().includes(q)
    );
  }, [requests, search]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-gray-800/60 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search by player, tag or clan…" />
      {!filtered.length ? (
        <div className="text-center py-16 text-gray-500">
          <div className="text-4xl mb-3">✅</div>
          <p>{search ? 'No requests match your search.' : 'No pending join requests.'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(req => (
            <RequestCard key={req.id} req={req} onApprove={onApprove} onReject={onReject} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ req, onApprove, onReject }) {
  const [role, setRole] = useState('member');
  const [confirm, setConfirm] = useState(null);

  const handleApproveClick = () => {
    setConfirm({
      title: 'Approve join request?',
      message: `Grant "${role}" access to ${req.player_name || req.player_tag || req.user_id} in ${req.clan_name}?`,
      confirmLabel: 'Approve',
      confirmClass: 'bg-green-600 hover:bg-green-500',
      onConfirm: () => { onApprove(req.id, role); setConfirm(null); },
    });
  };

  const handleRejectClick = () => {
    setConfirm({
      title: 'Reject join request?',
      message: 'This request will be permanently declined.',
      confirmLabel: 'Reject',
      confirmClass: 'bg-red-600 hover:bg-red-500',
      onConfirm: () => { onReject(req.id); setConfirm(null); },
    });
  };

  const dateTime = req.created_at
    ? new Date(req.created_at).toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-5 space-y-3">
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-100 truncate">
            {req.player_name || req.player_tag || req.user_id}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
            {req.player_tag && <span className="font-mono">{req.player_tag}</span>}
            {req.clan_name && (
              <>
                <span className="text-gray-700">·</span>
                <span>{req.clan_name}</span>
              </>
            )}
            {req.clan_tag && <span className="font-mono text-gray-600">{req.clan_tag}</span>}
            {dateTime && (
              <>
                <span className="text-gray-700">·</span>
                <span>{dateTime}</span>
              </>
            )}
          </div>
        </div>
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="text-xs bg-gray-900 border border-gray-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      {req.message && (
        <div className="bg-gray-900/60 border border-gray-700/60 rounded-xl px-4 py-3 text-sm text-gray-300 italic">
          "{req.message}"
        </div>
      )}
      <div className="flex gap-2 justify-end pt-1">
        <button
          onClick={handleRejectClick}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-400 bg-red-900/20 hover:bg-red-900/40 border border-red-700/40 rounded-lg transition-colors"
        >
          <IconX /><span>Reject</span>
        </button>
        <button
          onClick={handleApproveClick}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-green-400 bg-green-900/20 hover:bg-green-900/40 border border-green-700/40 rounded-lg transition-colors"
        >
          <IconCheck /><span>Approve as {role}</span>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ManageClansPage() {
  const navigate = useNavigate();
  const { isOwner, loading: authLoading, profileLoaded, refreshProfile } = useAuth();
  const { t, lang, setLang } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('clans');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => setToast({ msg, type }), []);

  React.useEffect(() => {
    // Only redirect after auth AND profile are fully loaded
    if (!authLoading && profileLoaded && !isOwner) {
      navigate('/', { replace: true });
    }
  }, [authLoading, profileLoaded, isOwner, navigate]);

  const { data: clansData, isLoading: clansLoading } = useQuery({
    queryKey: ['owner_clans'],
    queryFn: () => apiGet('/v1/owner/clans', { withAuth: true }),
    enabled: isOwner,
    staleTime: 60_000,
  });

  const { data: requestsData, isLoading: requestsLoading } = useQuery({
    queryKey: ['owner_requests'],
    queryFn: () => apiGet('/v1/owner/requests', { withAuth: true }),
    enabled: isOwner,
    staleTime: 30_000,
  });

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['owner_members'],
    queryFn: () => apiGet('/v1/owner/members', { withAuth: true }),
    enabled: isOwner,
    staleTime: 30_000,
  });

  const approveMutation = useMutation({
    mutationFn: ({ requestId, role }) =>
      apiPost(`/v1/owner/requests/${requestId}/approve`, { role }, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner_requests'] });
      queryClient.invalidateQueries({ queryKey: ['owner_members'] });
      refreshProfile();
      showToast('Request approved successfully', 'success');
    },
    onError: err => showToast(`Approval failed: ${err.message}`, 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ requestId }) =>
      apiPost(`/v1/owner/requests/${requestId}/reject`, {}, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner_requests'] });
      queryClient.invalidateQueries({ queryKey: ['owner_members'] });
      showToast('Request rejected', 'success');
    },
    onError: err => showToast(`Rejection failed: ${err.message}`, 'error'),
  });

  const banMutation = useMutation({
    mutationFn: ({ userId }) =>
      apiPost(`/v1/owner/users/${userId}/ban`, {}, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner_members'] });
      showToast('User banned', 'success');
    },
    onError: err => showToast(`Ban failed: ${err.message}`, 'error'),
  });

  const unbanMutation = useMutation({
    mutationFn: ({ userId }) =>
      apiPost(`/v1/owner/users/${userId}/unban`, {}, { withAuth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner_members'] });
      showToast('User unbanned', 'success');
    },
    onError: err => showToast(`Unban failed: ${err.message}`, 'error'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ clanId, isActive }) =>
      apiPost(`/v1/owner/clans/${clanId}/toggle-active`, { is_active: isActive }, { withAuth: true }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['owner_clans'] });
      showToast(vars.isActive ? 'Clan data fetching resumed' : 'Clan data fetching paused', 'success');
    },
    onError: err => showToast(`Toggle failed: ${err.message}`, 'error'),
  });

  const deleteClanMutation = useMutation({
    mutationFn: ({ clanId, slug }) =>
      apiPost(`/v1/owner/clans/${clanId}/delete-data`, { confirmation: `delete ${slug}` }, { withAuth: true }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['owner_clans'] });
      queryClient.invalidateQueries({ queryKey: ['owner_members'] });
      queryClient.invalidateQueries({ queryKey: ['owner_requests'] });
      showToast(data?.data?.message || 'Clan data deleted successfully', 'success');
    },
    onError: err => showToast(`Delete failed: ${err.message}`, 'error'),
  });

  const handleApprove = useCallback((requestId, role) => approveMutation.mutate({ requestId, role }), [approveMutation]);
  const handleReject  = useCallback((requestId) => rejectMutation.mutate({ requestId }), [rejectMutation]);
  const handleBan     = useCallback((userId) => banMutation.mutate({ userId }), [banMutation]);
  const handleUnban   = useCallback((userId) => unbanMutation.mutate({ userId }), [unbanMutation]);
  const handleToggleActive = useCallback((clanId, isActive) => toggleActiveMutation.mutate({ clanId, isActive }), [toggleActiveMutation]);
  const handleDeleteClan = useCallback((clanId, slug) => deleteClanMutation.mutate({ clanId, slug }), [deleteClanMutation]);

  const clans    = Array.isArray(clansData) ? clansData : [];
  const requests = requestsData?.requests || [];
  const members  = membersData?.members || [];
  const pendingCount = requests.length;

  if (authLoading || !profileLoaded) {
    return <div className="flex items-center justify-center h-screen bg-gray-900 text-gray-400">Loading…</div>;
  }
  if (!isOwner) return null;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {toast && (
        <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />
      )}

      <PageHeader lang={lang} setLang={setLang} t={t} />

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Breadcrumbs items={[
          { label: t('breadcrumb_home') || 'Home', path: '/' },
          { label: t('breadcrumb_manage_clans') || 'Manage Clans', path: null },
        ]} />
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-100">{t('manage_clans_title') || 'Clan Management'}</h1>
          <p className="text-sm text-gray-400 mt-1">{t('manage_clans_subtitle') || 'Owner-only — configure clans and process join requests'}</p>
        </div>

        <div className="flex gap-1 bg-gray-800/60 border border-gray-700 rounded-xl p-1 mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 text-sm rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white font-medium shadow'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.id === 'requests' && pendingCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === 'requests' ? 'bg-white/20 text-white' : 'bg-red-600 text-white'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'clans' && (
          <ClansTab clans={clans} loading={clansLoading} onToggleActive={handleToggleActive} onDelete={handleDeleteClan} t={t} />
        )}
        {activeTab === 'requests' && (
          <RequestsTab
            requests={requests}
            loading={requestsLoading}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
        {activeTab === 'members' && (
          <MembersTab
            members={members}
            loading={membersLoading}
            onBan={handleBan}
            onUnban={handleUnban}
          />
        )}
      </div>
    </div>
  );
}
